import { roleAtLeast, type UserRole } from '@bartizo/shared';
import type { ComponentType, SVGProps } from 'react';
import {
  ActivityIcon,
  BanIcon,
  CheckIcon,
  BellIcon,
  BulbIcon,
  ChatIcon,
  FilterIcon,
  GridIcon,
  HistoryIcon,
  KeyIcon,
  LayersIcon,
  LinkIcon,
  ServerIcon,
  ShieldIcon,
  SlidersIcon,
  SparkIcon,
  UserIcon,
  UsersIcon,
} from '../ui/icons.js';

/**
 * The navigation, and the role each destination needs (knowledge doc section 17).
 *
 * Data rather than markup, so "each role sees only what section 17 allows" is a
 * question a test can ask directly instead of by rendering a shell and reading the
 * links out of it.
 *
 * Hiding a link is a courtesy, never a control. Every one of these destinations is
 * enforced again by the API's own `requireRole`, because a hidden link is still a
 * URL anyone can type.
 */

/**
 * Where an item sits (P11.4): what is happening, what to do about it, how the fleet
 * is set up, and who may use it. `account` is not listed with the rest — it is the
 * person's own name at the foot of the sidebar.
 */
export type NavSection = 'monitor' | 'respond' | 'configure' | 'admin' | 'account';

export const NAV_SECTION_LABEL: Record<Exclude<NavSection, 'account'>, string> = {
  monitor: 'Monitor',
  respond: 'Respond',
  configure: 'Configure',
  admin: 'Admin',
};

export interface NavItem {
  to: string;
  label: string;
  /** The lowest role that may see this at all. */
  minimumRole: UserRole;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  section: NavSection;
  /** Which part of the build plan fills the page in. */
  arrivesIn?: string;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'Overview', minimumRole: 'viewer', icon: GridIcon, section: 'monitor' },
  { to: '/servers', label: 'Servers', minimumRole: 'viewer', icon: ServerIcon, section: 'monitor' },
  { to: '/events', label: 'Events', minimumRole: 'viewer', icon: ShieldIcon, section: 'monitor' },
  /** Section 8.7's Traffic page: section 17's "view" row, like events and metrics. */
  {
    to: '/traffic',
    label: 'Traffic',
    minimumRole: 'viewer',
    icon: ActivityIcon,
    section: 'monitor',
  },
  /** P11.8.9: the assessment through PCI DSS, CIS, NIST, HIPAA…; reading only. */
  {
    to: '/compliance',
    label: 'Compliance',
    minimumRole: 'viewer',
    icon: CheckIcon,
    section: 'monitor',
  },
  /** The fixes Bartizo makes, and where each stands on each server. */
  {
    to: '/tasks',
    label: 'Server tasks',
    minimumRole: 'viewer',
    icon: CheckIcon,
    section: 'monitor',
  },
  /** Section 12's bans and unbans. Reading them is anyone's; asking is an operator's. */
  { to: '/actions', label: 'Actions', minimumRole: 'viewer', icon: BanIcon, section: 'respond' },
  /** Section 12's block rules: read by anyone, changed by operators (P11.5). */
  { to: '/rules', label: 'Rules', minimumRole: 'viewer', icon: FilterIcon, section: 'respond' },
  /** Section 12's fleet blocklist: read by anyone, changed by operators (P11.6). */
  {
    to: '/blocklist',
    label: 'Blocklist',
    minimumRole: 'viewer',
    icon: BanIcon,
    section: 'respond',
  },
  /** Section 13's AI suggestions: read by anyone who reads events, decided by operators (P9.3). */
  {
    to: '/suggestions',
    label: 'Suggestions',
    minimumRole: 'viewer',
    icon: BulbIcon,
    section: 'respond',
  },
  /** Section 13's questions: asking spends the budget, so an operator's (P9.3). */
  { to: '/ask', label: 'Ask', minimumRole: 'operator', icon: ChatIcon, section: 'respond' },
  /** Section 7's groups: the middle settings layer, so an admin's like Settings (P11.5). */
  { to: '/groups', label: 'Groups', minimumRole: 'admin', icon: LayersIcon, section: 'configure' },
  {
    to: '/settings',
    label: 'Settings',
    minimumRole: 'admin',
    icon: SlidersIcon,
    section: 'configure',
  },
  /** Section 15's notification settings: the email server and the rules (P9.1). */
  {
    to: '/notifications',
    label: 'Notifications',
    minimumRole: 'admin',
    icon: BellIcon,
    section: 'configure',
  },
  /** Section 13's providers, routing, budget and masking: section 17 gives them to admins (P9.2). */
  { to: '/ai', label: 'AI', minimumRole: 'admin', icon: SparkIcon, section: 'configure' },
  /** Captcha keys, Cloudflare and CrowdSec CTI (P11.9.3, P11.9.5, P11.9.6). */
  {
    to: '/integrations',
    label: 'Integrations',
    minimumRole: 'admin',
    icon: LinkIcon,
    section: 'configure',
  },
  {
    to: '/enrollment-tokens',
    label: 'Enrollment tokens',
    minimumRole: 'admin',
    icon: KeyIcon,
    section: 'configure',
  },
  /** Section 10's other method: no token, one server, a code read off its terminal. */
  { to: '/pairing', label: 'Pairing', minimumRole: 'admin', icon: LinkIcon, section: 'configure' },
  /** Rule 9's record. An entry carries addresses and settings, so admins only (P8.3). */
  {
    to: '/audit-log',
    label: 'Audit log',
    minimumRole: 'admin',
    icon: HistoryIcon,
    section: 'admin',
  },
  /** Section 17's admin row: who may sign in, and at what role (P8.4). */
  { to: '/users', label: 'Users', minimumRole: 'admin', icon: UsersIcon, section: 'admin' },
  /** Everyone has an account to look after, whatever their role. */
  {
    to: '/account',
    label: 'Your account',
    minimumRole: 'viewer',
    icon: UserIcon,
    section: 'account',
  },
];

export const visibleNavItems = (role: UserRole): NavItem[] =>
  NAV_ITEMS.filter((item) => roleAtLeast(role, item.minimumRole));

/** The role a path needs, or `undefined` for a path that is not in the navigation. */
export const roleForPath = (path: string): UserRole | undefined =>
  NAV_ITEMS.find((item) => item.to === path)?.minimumRole;
